import { ReactElement } from 'react'
import { useFormContext, useWatch, useController } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Grid, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { CheckboxLabeled } from '@ui/actions'
import { Label, Select } from '@ui/forms'
import { getGlobalFireSettings } from '@queries'
import {
  CheckSettingsFormFieldsCommonProps,
  compactCheckSettingsFormSx,
  getCheckSettingsMaxWidth,
  getCheckSettingsStackSpacing,
} from '../checkSettingsFormFieldLayout'
import { DefaultValues } from '../types'

const MEMBER_SETTINGS_ROOT = 'memberCheckSettingsSelectedElement' as const

interface Props extends CheckSettingsFormFieldsCommonProps {
  /** Registered field path prefix for settings on member (nested forms use `elements.0.${MEMBER_SETTINGS_ROOT}`). */
  memberCheckSettingsSelectedPath?: string
}

const HotDimensioningCheckSettingsFormFields = ({
  disabled,
  compact,
  memberCheckSettingsSelectedPath = MEMBER_SETTINGS_ROOT,
}: Props): ReactElement | null => {
  const { projectId }: { projectId?: string } = useParams()

  const { setValue } = useFormContext()

  const isHotDimensioningEnabled = useWatch({
    name: `${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.make_hot_dimensioning_checks`,
  })

  const { data: globalFireSettings } = useQuery(getGlobalFireSettings.getKey(projectId), () =>
    getGlobalFireSettings.request(projectId),
  )

  const settingsOnMember: SettingsOnMember = useWatch<DefaultValues>({
    name: memberCheckSettingsSelectedPath as 'memberCheckSettingsSelectedElement',
  })

  if (!('hot_dimensioning_settings' in settingsOnMember))
    throw new Error('Hot dimensioning not available')

  // Wrapper component to integrate CheckboxLabeled with react-hook-form
  const FormCheckboxLabeled = ({
    name,
    label,
    customOnChange,
    disabled: checkboxDisabled,
  }: {
    name: string
    label: string
    customOnChange?: (checked: boolean) => void
    disabled?: boolean
  }) => {
    const { control } = useFormContext()
    const {
      field: { onChange, value },
    } = useController({ name, control })

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked
      onChange(checked)
      customOnChange?.(checked)
    }

    return (
      <CheckboxLabeled
        checked={value}
        onChange={handleChange}
        label={label}
        disabled={checkboxDisabled}
      />
    )
  }

  return (
    <Stack
      direction="column"
      spacing={getCheckSettingsStackSpacing(compact)}
      maxWidth={getCheckSettingsMaxWidth(compact)}
      sx={compact ? compactCheckSettingsFormSx : undefined}
    >
      <FormCheckboxLabeled
        name={`${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.make_hot_dimensioning_checks`}
        label="Heißbemessung durchführen"
        disabled={disabled}
        customOnChange={e => {
          if (!e) {
            setValue(
              `${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_duration`,
              null,
            )
            setValue(
              `${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_exposed_sides`,
              null,
            )
          } else if (e && globalFireSettings) {
            setValue(
              `${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_duration`,
              globalFireSettings.hot_dimensioning_settings.fire_duration,
            )
            setValue(
              `${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_exposed_sides`,
              {
                top: true,
                bottom: true,
                left: true,
                right: true,
              },
            )
          }
        }}
      />

      {isHotDimensioningEnabled && (
        <Grid container>
          <Grid item xs={12}>
            <Select
              label="Feuerwiderstandsklasse"
              name={`${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_duration`}
              disabled={disabled}
              options={[
                { value: 0, label: 'F0' },
                { value: 1800, label: 'F30' },
                { value: 3600, label: 'F60' },
                { value: 5400, label: 'F90' },
                { value: 7200, label: 'F120' },
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            {compact ? (
              <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.25 }}>
                Brandbeanspruchte Seiten
              </Typography>
            ) : (
              <Label>Brandbeanspruchte Seiten</Label>
            )}
          </Grid>
          <Grid container alignSelf="center" padding={0}>
            {/* Top row */}
            <Grid item xs={4}></Grid>
            <Grid item xs={4} display="flex" justifyContent="center">
              <FormCheckboxLabeled
                name={`${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_exposed_sides.top`}
                label="oben"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4}></Grid>

            {/* Middle row */}
            <Grid item xs={4} display="flex" justifyContent="center">
              <FormCheckboxLabeled
                name={`${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_exposed_sides.left`}
                label="links"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4}></Grid>
            <Grid item xs={4} display="flex" justifyContent="center">
              <FormCheckboxLabeled
                name={`${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_exposed_sides.right`}
                label="rechts"
                disabled={disabled}
              />
            </Grid>

            {/* Bottom row */}
            <Grid item xs={4}></Grid>
            <Grid item xs={4} display="flex" justifyContent="center">
              <FormCheckboxLabeled
                name={`${memberCheckSettingsSelectedPath}.hot_dimensioning_settings.fire_exposed_sides.bottom`}
                label="unten"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={4}></Grid>
          </Grid>
        </Grid>
      )}
    </Stack>
  )
}

export default HotDimensioningCheckSettingsFormFields
